import React from 'react';
import cx from 'classnames';
import ReactECharts from 'echarts-for-react';
import { Progress, Pagination, ConfigProvider } from 'antd';

import { IReportPageData } from '@/constants/geo';
import SVGLeftArrow from '@/assets/geo/pc/leftArrow.svg?react';
import SVGRightArrow from '@/assets/geo/pc/rightArrow.svg?react';
import LongText from '@/components/LongText';
import IntroduceTip from '@/components/IntroduceTip';
import ReportLayout from '@/components/ReportLayout/ReportLayout';

import { getVisibilityChartOption, getDonutOption, getStackedBarOption } from '../../chartConfig/chartOptions';
import styles from './index.module.less';

/** 倾向性标签 */
const INCLINATION_LABELS = ['积极', '中立', '消极'];
/** 饼图样式 */
const DONUT_STYLES: React.CSSProperties = {
  height: 238,
  width: '100%',
};

/** 柱状图样式 */
const BAR_STYLES: React.CSSProperties = {
  height: 220,
  width: '100%',
};

interface IProps {
  pageData: IReportPageData;
  setPageData?: (data: Partial<IReportPageData>) => void;
}

const PcReport: React.FC<IProps> = (props) => {
  const { pageData, setPageData } = props;
  const {
    aiPlatformNameList,
    isFewPlatforms,
    aiInclinationNumber,
    brandCompetitivenessData,
    aiVisibilityPlatformNumberData,
    aiInclinationPlatformChartData,
    brandCompetitivenessPlatformChartData,
  } = pageData;

  /** 渲染左侧圆形图 */
  const renderRound = (config: { title: string; desc: string; roundNode: React.ReactNode }) => {
    const { title, desc, roundNode } = config;
    return (
      <div className={cx(styles['pr_part'], leftRoundClass)}>
        <div className={styles['pr_common_title_desc_wrap']}>
          <div className={styles['pr_common_title_wrap']}>
            <div className={styles['pr_common_title']}>{title}</div>
            <IntroduceTip title={desc} />
          </div>
        </div>
        <div>{roundNode}</div>
      </div>
    );
  };

  /** 渲染AI可见性 */
  const renderRoundContent = () => {
    return (
      <div className={styles['pr_visibility_wrap']} style={DONUT_STYLES}>
        <Progress
          type="circle"
          // percent={pageData.aiVisibilityNumber}
          percent={100}
          size={170}
          strokeWidth={8}
          format={() => (
            <div className={styles['pr_visibility_inner']}>
              <div className={styles['pr_visibility']}>
                <div className={styles['pr_visibility_value']}>{pageData.aiVisibilityNumber}</div>
                {/* <div className={styles['pr_visibility_unit']}>%</div> */}
              </div>
            </div>
          )}
          strokeColor="#FA8846"
          railColor="#DFE0E5"
        />
      </div>
    );
  };

  /** 渲染左侧饼图 */
  const renderPieChart = (config: { data: number[]; legend: string[]; colors?: string[] }) => {
    const { data, legend, colors } = config;
    return (
      <div className={styles['pr_pie_chart_wrap']} style={DONUT_STYLES}>
        <ReactECharts
          option={getDonutOption({ data, legend, colors })}
          className={styles['pr_pie_chart']}
          style={DONUT_STYLES}
        />
        <div className={styles['pr_pie_center']}>
          <div className={styles['pr_pie_center_value_wrap']}>
            <div className={styles['pr_pie_center_value']}>{data[0]}</div>
            <div className={styles['pr_pie_center_unit']}>%</div>
          </div>
          <div className={styles['pr_pie_center_legend']}>{legend[0]}占比</div>
        </div>
      </div>
    );
  };

  /** 左侧饼图 class */
  const leftRoundClass = isFewPlatforms ? styles['pr_round_less'] : styles['pr_round_more'];
  /** 右侧图表 class */
  const rightChartClass = isFewPlatforms ? styles['pr_chart_less'] : styles['pr_chart_more'];

  return (
    <>
      <div className={styles['pr_part_wrap']}>
        {renderRound({
          title: '品牌搜索指数',
          desc: 'AI平台搜索时的品牌热度、信息权重和可见度综合计算结果',
          roundNode: renderRoundContent(),
        })}
        <div className={cx(styles['pr_part'], rightChartClass)}>
          <div className={styles['pr_common_title']}>各平台曝光情况</div>
          <ReactECharts
            option={getVisibilityChartOption({
              legend: aiPlatformNameList,
              data: aiVisibilityPlatformNumberData,
            })}
            style={BAR_STYLES}
          />
        </div>
      </div>
      <div className={styles['pr_part_wrap']}>
        {renderRound({
          title: '品牌舆情度',
          desc: 'AI推荐时的正面、负面、中立评价信息占比',
          roundNode: renderPieChart({
            data: [aiInclinationNumber.positivePercent, aiInclinationNumber.neutralPercent, aiInclinationNumber.negativePercent],
            legend: INCLINATION_LABELS,
            colors: ['#FA8846', '#5D8AF2', '#666A80'],
          }),
        })}
        <div className={cx(styles['pr_part'], rightChartClass)}>
          <div className={styles['pr_common_title']}>各平台推荐情况</div>
          <ReactECharts
            option={getStackedBarOption({
              legend: aiPlatformNameList,
              data: aiInclinationPlatformChartData,
            })}
            style={BAR_STYLES}
          />
        </div>
      </div>
      <div className={styles['pr_part_wrap']}>
        {renderRound({
          title: '品牌竞争度',
          desc: 'AI推荐时品牌在TOP品牌中的信息占比',
          roundNode: renderPieChart({
            data: brandCompetitivenessData.map((item) => item.percentage),
            legend: brandCompetitivenessData.map((item) => item.name),
          }),
        })}
        <div className={cx(styles['pr_part'], rightChartClass)}>
          <div className={styles['pr_common_title']}>各平台竞争情况</div>
          <ReactECharts
            option={getStackedBarOption({
              legend: aiPlatformNameList,
              data: brandCompetitivenessPlatformChartData,
            })}
            style={BAR_STYLES}
          />
        </div>
      </div>
    </>
  );
};

export default PcReport;
